import type { IHasCategories } from "@biz/assets/AssetDefinitions";
import type { IGlossyInfo, PowderCoatingCategory } from "./PowderCoatingDefinitions";
import type { Access, DealerType } from "./AccessDefinitions";
import type { ClothPatterns } from "@biz/SunSailColors";
import type { ILocaContext } from "@biz/language/LocaContext";
import { getKeys, mapRecord, PartialRecord, TupleToUnion } from "@base/util/TypeUtil";
import { languageFallbacks } from "@biz/language/Languages";

import type { PointMountType } from "./MountDefinitons";

import type { ArticleGroup, IBeosOrderProperties, IPartListProperties, IShoppingArticleOptions, QuickCategory } from "@biz/models/ShoppingListDefinitions";
import type { EdgeName, MaterialType, PoleId, ProductionLocation, RopeDiameter } from "@biz/definitions/SunSailDefinitions";
import type { PriceListIndex } from "@biz/definitions/PriceDefinitions";
import type { ArticleFeatureStore, IArticleFeatureTemplate } from "@biz/definitions/ArticleFeatureDefinitions";
import type { LanguageType, MimeType, Unit } from "@biz/Definitions";

export type GuideNotchSide = "L" | "R" | "LR";
export type LogoPrint = "none" | "solidayLogoRound";

export const ARTICLE_NUMBER_STONE = "935577";

export type RopeColor = "grey" | "blue" | "navy" | "purple" | "orange" | "red" | "green" | "black";
export type RopeType = "4" | "6.5" | "8" | "10" | "6.5E" | "8A" | "8E";
export type ShoppingArticleType = "obligatory" | "optional";

export const RAFF_PREMONTAGE = "00E13001784";

export type AdditionalArticleInfoFilter = "partList" | "order" | "posExtraText";
export interface IAdditionalArticleInfoItem {
  filter?: AdditionalArticleInfoFilter[];
  header?: string;
  text: string | string[];
  flag?: "important";
}

export interface ICustomArticleInfo {
  unit: string;
  name: string;
  desc: string;
  price: number;
}

export interface IArticleComputedInfo {
  polePosition?: number;
}

export type AdditionalArticleInfos = Record<string, IAdditionalArticleInfoItem>;
export type AdditionalArticleInfosOption = Record<string, IAdditionalArticleInfoItem | AdditionalArticleInfoItemFn | undefined | false | 0>;

export interface IShoppingRuleArticleCommon {
  id: string; // unique id
  count: number;
  countClientAvailable?: number;
  mul?: number;

  articleType: ShoppingArticleType;
  stkUnit?: Unit;
  mulUnit?: Unit;

  priceListIndex: PriceListIndex;

  articleNumber: string;
  articlePrice: number;
  disabled?: boolean;

  affiliation?: IArticleAffiliation,
  customArticle?: ICustomArticleInfo;
  powderCoating?: string;
  beosOrder?: IBeosOrderProperties;
  additionalArticleInfo?: AdditionalArticleInfos;

  offerList?: OfferListType;
  partList?: IPartListProperties;
  articleGroup?: ArticleGroup;

  computed?: IArticleComputedInfo;

  quickOverlayPriority?: number; // order QuickOverlay articles
  shoppingCartPriority?: number; // order Shopping Cart, Offer, Order, BeosOrderManager
}

export type AdditionalArticleInfoItemFn = (article: IShoppingArticle, context: { lc: ILocaContext }) => IAdditionalArticleInfoItem | undefined
export interface IRuleArticle extends IShoppingRuleArticleCommon {
  idMode: IShoppingArticleOptions["idMode"] | undefined;
  granularity?: number;
  maxUnits?: number;

  features?: ArticleFeatureStore;
  featureTemplate?: IArticleFeatureTemplate;

  included?: RuleArticleMap;

  boxed?: "multi";
  addToCart?: boolean;
  featureArticles?: IRuleArticle[];
  addedSlotTime?: number;
}

export interface IShoppingArticle extends IShoppingRuleArticleCommon {
  included?: Record<string, IShoppingArticle>;

  features?: ArticleFeatureStore;

  _handled?: boolean;
}

export interface IShoppingArticleWithBeosArticle extends IShoppingArticle {
  beos: IBeosArticle | undefined;
}

export type RuleArticleMap = Record<string, IRuleArticle>;

export function setDefaultArticleOfferList(a: IRuleArticle, defaultOfferList: OfferListType): IRuleArticle {
  return {
    ...a,
    included: a.included ? mapRecord(a.included, (item) => setDefaultArticleOfferList(item, defaultOfferList)) : undefined,
    offerList: a.offerList ? a.offerList : defaultOfferList
  }
}

export function mulArticles(a: IRuleArticle, count: number | undefined): IRuleArticle {
  if (count === 1 || count === undefined)
    return a;
  return {
    ...a,
    count: a.count * count
  }
}

export function findArticles(articles: RuleArticleMap, articleNumbers: string[]): { articles: IRuleArticle[]; count: number; } {
  const ret: IRuleArticle[] = [];
  for (const k of Object.keys(articles)) {
    const article = articles[k];
    if (articleNumbers.includes(article.articleNumber)) {
      ret.push(article);
    }
  }
  return {
    articles: ret,
    count: ret.reduce((p, c) => p + c.count, 0)
  };
}

export interface IArticleWithCount {
  articleNumber: string,
  count: number;
}

export interface IMountArticle {
  articleNumber: string,
  mountType: PointMountType;
  offerList?: OfferListType;
}

export interface IArticleGroup<T> {
  text: string;
  groupId: string;
  items: T[];
}

export interface IArticleExtraPrice {
  variantId: string;
  extraCharge: number;
  factor: number;
  factorP?: number;
}

export interface IArticleColorSmall extends IArticleExtraPrice {
  // is used in the app with this layout => maybe we can use IColorItem in the future...
  print?: boolean;
  specialColor?: boolean;
  style?: boolean;
  pattern?: ClothPatterns;
  description?: string;
  sunSailCloth?: boolean;
  name: string;
  rollWidth?: number;
  displayName: string;
  overrideId?: string;
}

// export interface IArticleColor extends IArticleColorSmall {

//   /** @deprecated */
//   color: string;
//   /** @deprecated */
//   priority: number;
//   /** @deprecated */
//   available: boolean;
//   /** @deprecated */
//   longerDelivery?: boolean;
// }

export interface IScalePrice {
  quantity: number;
  priceValues: number[];
}

export interface IRopeArticleAnnotation {
  ropeType: RopeType;
  colorId: RopeColor;
  diameter: RopeDiameter;
}

export interface IWinchArticleAnnotation {
  name: string;
}

export interface INameArticleAnnotation {
  name: string;
}

export function getRopeColorInfoById(ropeColor: RopeColor): { name: string; color: string } {
  switch (ropeColor) {
    case "grey":
      return { name: "silbergrau", color: "#808080" };
    case "blue":
      return { name: "beige/blau meliert", color: "#1193d4" };
    case "navy":
      return { name: "beige/navy meliert", color: "#080421" };
    case "purple":
      return { name: "beige/purple meliert", color: "#602577" };
    case "orange":
      return { name: "beige/orange meliert", color: "#ed6904" };
    case "red":
      return { name: "beige/red meliert", color: "#c32828" };
    case "green":
      return { name: "beige/green meliert", color: "#6cd371" };
    case "black":
      return { name: "beige/green meliert", color: "#333333" };
  }
}

// export enum TwistingSetVariant {
//   ALU = "ALU",
//   NIRO = "NIRO",
//   DROP1X = "DROP1X",
//   ALUALU = "ALUALU",
//   ALUDP = "ALUDP",
//   ALUNIRO = "ALUNIRO",
//   DPDP = "DPDP",
//   NIRODP = "NIRODP",
//   NIRONIR = "NIRONIR",
//   None = "None",
// }

export type RainCutVariant = "RAINCUT" | "RAINCU2" | "STANDAR" | "STANDA2";

export interface IArticleAffiliation {
  groupIndex?: number;
  poleId?: PoleId;
  patchIndex?: number;
  clothIndex?: number;
  trackIndex?: number;
  edgeName?: EdgeName;
  quickCategory?: QuickCategory;
}

export type ArticleListType = "stockList" | "sunSailList" | "hardwareList" | "systemList" | "colliDispatch" | "shoppingCart";
export const OfferListItems = ["H", "S", "KH", "KS", "N"] as const;
export type OfferListType = TupleToUnion<typeof OfferListItems>;
export type OfferListTypeFilter = PartialRecord<OfferListType, boolean>;

export type ArticleAvailability = "available" | "longerDelivery" | "onRequest" | "unavailable";
export const ArticleAvailabilities = ["available", "longerDelivery", "onRequest", "unavailable"];

export function getAvailability(availability: ArticleAvailability | undefined): ArticleAvailability {
  if (!availability)
    return "available";
  return availability;
}

export function availabilityToNumber(availability: ArticleAvailability | undefined): number {
  switch (availability) {
    case undefined: return 0;
    case "available": return 0;
    case "longerDelivery": return 1;
    case "onRequest": return 2;
    case "unavailable": return 3;
  }
  return 3;
}

export interface IBeosArticle {
  _id: string;
  articleId: string;
  name: string;
  localizedNames: PartialRecord<LanguageType, string>;
  desc: string;
  localizedDescriptions: PartialRecord<LanguageType, string>;
  productionText?: string;
  priceValues: number[];
  // unit: Unit;
  stkUnit?: Unit;
  mulUnit?: Unit;
  discountGroup: string;
  colorGroup?: IArticleGroup<IArticleColorSmall>;
  sizeGroup?: IArticleGroup<IArticleExtraPrice>;
  rainCutGroup?: IArticleGroup<IArticleExtraPrice>;
  tieBackLengthGroup?: IArticleGroup<IArticleExtraPrice>;
  twistingSetGroup?: IArticleGroup<IArticleExtraPrice>;
  scalePrices?: IScalePrice[];
  availability?: ArticleAvailability;

  powderCoating?: PowderCoatingCategory;
  powderCoatingMul?: number;
  glossy?: IGlossyInfo;
  clientAvailable?: boolean;
}

export interface IArticle extends IBeosArticle {
  maxUnits?: number;
  // partListName?: string;
  // shopPriority?: number; // quickOverlayPriority?
}

export function getArticleName(article: IBeosArticle, language: LanguageType): string {
  if (!article)
    return "";
  if (!article.localizedNames) // partList on server...?
    return article.name;
  const ls = [language, ...languageFallbacks];
  for (const lf of ls) {
    const f = article.localizedNames?.[lf];
    if (f)
      return f;
  }
  return "";
}

export function getSafeArticleName(articleMap: Record<string, IArticle>, articleNumber: string): string {
  const a = articleMap[articleNumber];
  if (!a)
    return articleNumber;
  return getArticleName(a, "de_AT");
}

export function getArticleDesc(article: IBeosArticle, language: LanguageType, fallback: boolean): string {
  if (!article)
    return "";

  if (!article.localizedDescriptions) // partList on server...?
    return article.desc;

  const ls = [language];
  if (fallback)
    ls.push(...languageFallbacks);
  for (const lf of ls) {
    const f = article.localizedDescriptions?.[lf];
    if (f)
      return f;
  }
  return "";
}

export interface IArticleCategoryInfo {
  priority: number;
}

export interface IArticleImageFileInfo {
  size: number;
  lastModified: number; // File
  mimeType: MimeType;
  hash?: string;
  url: string;
}

export interface IArticleImage {
  small: IArticleImageFileInfo;
  big: IArticleImageFileInfo;
  thumb: IArticleImageFileInfo;
}

export interface IArticleImageUrls {
  small: string;
  big: string;
  thumb: string;
}

export interface IAppArticle extends IHasCategories<IArticleCategoryInfo> {
  _lastModified: number;
  maxUnits?: number;
  availability?: ArticleAvailability;
  images?: IArticleImage[];
  partListName?: string;

  access?: Access;
  dealerTypes?: DealerType[];

  powderCoating?: PowderCoatingCategory;
  powderCoatingMul?: number;
  glossy?: IGlossyInfo,
  clientAvailable?: boolean;
  badgeText?: string;
  weight?: number;
  length?: number;
  locations?: ProductionLocation[];

  // !!! also add to stripAppArticle !!!
}

// remove possible other fields from e.g. IMergedArticle...
// object is filtered on server => for additional fields - server has to be recompiled...
export function stripAppArticle(appArticle: IAppArticle): IAppArticle {
  const ret: IAppArticle = {
    _id: appArticle._id,
    _lastModified: appArticle._lastModified,
    categories: appArticle.categories,
    maxUnits: appArticle.maxUnits,
    availability: appArticle.availability,
    images: appArticle.images,

    partListName: appArticle.partListName,
    powderCoating: appArticle.powderCoating,
    powderCoatingMul: appArticle.powderCoatingMul,
    glossy: appArticle.glossy,
    clientAvailable: appArticle.clientAvailable,
    badgeText: appArticle.badgeText,

    access: appArticle.access,
    dealerTypes: appArticle.dealerTypes,
    weight: appArticle.weight,
    length: appArticle.length,
    locations: appArticle.locations,
  };

  return ret;
}

const poleArticles: PartialRecord<MaterialType, Record<number, string>> = {
  "Droppole": { 3: "00E13000335", 4: "00E13000336", 5: "00E13000337", 6: "00E13000387" },
  "Droppole_Black": { 3: "00E13000338", 4: "00E13000347", 5: "00E13000348", 6: "00E13000339" },
  "Alu": { 3: "00E13000235", 6: "00E13000238" },
  "AluWood": { 3: "00E13000981", 6: "00E13000982" },
  "Alu_Black": { 3: "00E13001650", 6: "00E13001652" },
  "Steel": { 3: "00E13000135", 6: "00E13000138" },
  "Steel_V4A": { 3: "00E13001766", 6: "00E13001767" },
  "Iron48": { 3: "60001B001", 6: "60001A003" },
  "Iron114": { 3: "00E13000831", 6: "00E13000832" },
  "Iron89": { 3: "00E13001654", 6: "00E13001655" },
  "Iron89_Black": { 3: "00E13001656", 6: "00E13001657" },
  "Telescope": { 3: "00E13000600" },
  "TelescopeWood": { 3: "00E13000600" },
  "Telescope_Black": { 3: "60AZUBS038" }, // { 3: "60AZUBS038" },
  "Profile": { 3: "00E13001745", 4: "00E13001747" }, // { 3: "00E13001745", 4: "00E13001747" },
  "Profile_Black": { 3: "00E13001746", 4: "00E13001748" }, // { 3: "00E13001746", 4: "00E13001748" },
}

const allPoleArticles: Record<string, MaterialType> = {}
for (const k of getKeys(poleArticles)) {
  for (const k2 of getKeys(poleArticles[k])) {
    const articleId = poleArticles[k]?.[k2];
    if (articleId)
      allPoleArticles[articleId] = k;
  }
}

export function getPoleArticles(material: MaterialType): Record<number, string> {
  const articles = poleArticles[material];
  if (!articles)
    throw new Error("Articles not supported!");
  return articles;
}

export function getPoleArticle(material: MaterialType, length: number): string {
  const l = Math.ceil(length);
  const articles = getPoleArticles(material);
  if (articles[l])
    return articles[l];

  const keys = Object.keys(articles);
  for (const k of keys) {
    if (+k >= l)
      return articles[+k];
  }
  return articles[+keys[keys.length - 1]];
}


function getPylonMaterialId(material: MaterialType): string {
  switch (material) {
    case "Droppole":
      return "DP0";
    case "Droppole_Black":
      return "DP1";
    case "Alu":
      return "AL0";
    case "AluWood":
      return "AL0";
    case "Alu_Black":
      return "AL1";
    case "Steel":
      return "NI0";
    case "Steel_V4A":
      return "NI1";
  }
  return "";
}

export function getPylonArticleId(material: MaterialType, chargedLength: number, pylonType: "ZT1000" | "ZT700" | "ZT500"): string {
  const mat = getPylonMaterialId(material);
  return "PYL" + mat + chargedLength + pylonType.substring(2);  // eg PYLDP031000
}

export function getPolyOrPylonArticleId(material: MaterialType, chargedLength: number, pylonType: "ZT1000" | "ZT700" | "ZT500" | undefined): string {
  if (!pylonType) {
    const article = getPoleArticle(material, chargedLength)
    return article;
  } else {
    return getPylonArticleId(material, chargedLength, pylonType);
  }
}

export function isPoleArticleId(articleId: string): boolean {
  if (articleId.startsWith("PYL"))
    return true;
  if (allPoleArticles[articleId])
    return true;
  return false;
}

export interface IQuickItem<T = string> {
  value: T;
  label: string | React.ReactElement;
  pointColor?: string;
  image?: string;
  color?: string;
  badge?: string;
  onClick?: () => void;
  infoItem?: React.ReactElement | undefined;
  articleNumber?: string;
  article?: {
    articleNumber: string;
    price: string;
  },
  disabled?: boolean;
  // availability?: QuickAvailability;
}

export interface IQuickColorItem {
  id: string;
  label: string | React.ReactElement;
  pointColor?: string;
  color: string;
  priority?: number;
  image?: string;
  patternImage?: string;
  onClick?: () => void;
  availability?: ArticleAvailability;
}

export interface IShoppingListItemHandlerAdditional {
  articleNumber: string | undefined;
  mul: number | undefined;
  features: ArticleFeatureStore | undefined;
  affiliation: IArticleAffiliation | undefined;
}
export interface IShoppingListItemHandler {
  onSetItemCount: (id: string, count: number, additional: IShoppingListItemHandlerAdditional) => void;
  onMoveItemCount: (oldArticleId: string, newArticleId: string, count: number, additional: IShoppingListItemHandlerAdditional) => void;
  showNotification?: (id: string, articleNumber: string, changeInfo: IShoppingCartChangeInfo) => void;
}

export interface IShoppingCartChangeInfo {
  delta: number;
  minReached: number | undefined;
  maxReached: number | undefined;
}